/**
 * Business Quick — Login + Dashboard + New Booking + Detail/Label
 * Phase 2G (2026-04-25/26)
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Loader2, Building2, Package, Banknote, Plus, ArrowLeft,
  Printer, Send, RefreshCw, CreditCard, Clock, CheckCircle2, AlertCircle,
} from "lucide-react";

function statusPill(p) {
  const ps = (p.payment_status || "").toLowerCase();
  const st = (p.status || "").toLowerCase();
  if (ps === "paid" && st !== "delivered") {
    return <span className="text-xs bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">PAID · {st}</span>;
  }
  if (ps === "pending") {
    return <span className="text-xs bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">AWAITING PAYMENT</span>;
  }
  if (st === "delivered") {
    return <span className="text-xs bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">DELIVERED</span>;
  }
  return <span className="text-xs bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">{st || ps}</span>;
}

function logout(nav) {
  localStorage.removeItem("token");
  localStorage.removeItem("bq_business");
  nav("/business-quick/login");
}

export function BusinessQuickLogin() {
  const nav = useNavigate();
  const [identifier, setId] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!identifier || !pin) return toast.error("Enter phone/email and PIN");
    setBusy(true);
    try {
      const r = await api.post("/business-quick/auth/login", { identifier, pin });
      localStorage.setItem("token", r.data.token);
      localStorage.setItem("bq_business", JSON.stringify(r.data.business));
      toast.success(`Welcome ${r.data.business.business_name}`);
      nav("/business-quick/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="business-quick-login-page">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 w-full max-w-sm p-6">
        <div className="flex items-center gap-2 mb-1">
          <Building2 className="w-5 h-5 text-emerald-600" />
          <h1 className="text-lg font-bold">Business Quick Booking</h1>
        </div>
        <p className="text-xs text-slate-500 mb-4">No cash accepted at PUDO. All packages must be paid online before pickup.</p>
        <div className="space-y-3">
          <Input data-testid="bq-login-id" placeholder="Phone or email" value={identifier} onChange={e => setId(e.target.value)} />
          <Input data-testid="bq-login-pin" placeholder="6-digit PIN" type="password" value={pin} onChange={e => setPin(e.target.value)} />
          <Button data-testid="bq-login-submit" onClick={submit} disabled={busy} className="w-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Login
          </Button>
          <button data-testid="bq-forgot-pin" onClick={async () => {
            if (!identifier) return toast.error("Enter phone/email first");
            await api.post("/business-quick/auth/request-pin-reset", { identifier });
            toast.success("If your account exists, a reset PIN has been emailed.");
          }} className="text-xs text-slate-500 underline">Forgot PIN</button>
        </div>
      </div>
    </div>
  );
}

function BizHeader({ title, right }) {
  const nav = useNavigate();
  const biz = (() => { try { return JSON.parse(localStorage.getItem("bq_business") || "{}"); } catch { return {}; } })();
  return (
    <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-emerald-600" />
          <div>
            <div className="text-sm font-bold leading-none">{title}</div>
            <div className="text-[11px] text-slate-500">{biz.business_name || "Business"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {right}
          <button onClick={() => logout(nav)} data-testid="bq-logout" className="text-xs text-slate-500 underline">Logout</button>
        </div>
      </div>
    </div>
  );
}

export function BusinessQuickDashboard() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState("all");

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (filter === "pending") params.payment_status = "pending";
      if (filter === "paid") params.payment_status = "paid";
      const r = await api.get("/business-quick/packages", { params });
      setRows(r.data.rows || []);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        toast.error("Session expired");
        logout(nav);
      } else {
        toast.error("Could not load packages");
      }
    } finally { setBusy(false); }
  }, [filter, nav]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="business-quick-dashboard">
      <BizHeader title="My packages" right={
        <Button data-testid="bq-new-package" size="sm" onClick={() => nav("/business-quick/book")}>
          <Plus className="w-4 h-4 mr-1" />New
        </Button>
      } />
      <div className="max-w-2xl mx-auto px-4 py-3">
        <div className="flex gap-2 mb-3">
          {["all", "pending", "paid"].map(f => (
            <button key={f} data-testid={`bq-filter-${f}`} onClick={() => setFilter(f)}
              className={`text-xs px-3 py-1 rounded-full border ${filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white"}`}>
              {f.toUpperCase()}
            </button>
          ))}
          <button onClick={reload} className="text-xs px-3 py-1 rounded-full border bg-white ml-auto">
            <RefreshCw className="w-3 h-3 inline" /> Refresh
          </button>
        </div>
        {busy ? (
          <div className="text-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-1" />Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center text-slate-500" data-testid="bq-empty">
            <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No packages yet. Tap <strong>+ New</strong> to create your first one.
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map(p => (
              <Link key={p.id} to={`/business-quick/package/${p.id}`} data-testid={`bq-row-${p.id}`}
                className="block bg-white border border-slate-200 rounded-xl p-3 hover:border-emerald-300 transition">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm">{p.tracking_number}</div>
                  {statusPill(p)}
                </div>
                <div className="text-xs text-slate-600 mt-1">→ {p.destination_pudo_name} · {p.destination_pudo_parish}</div>
                <div className="text-xs text-slate-500">{p.receiver_name} · {p.receiver_phone}</div>
                <div className="text-xs text-slate-500">{p.weight_lbs} lb · J${p.price} · {p.payment_responsibility}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const PARISHES = [
  "Kingston", "St. Andrew", "St. Catherine", "Clarendon", "Manchester",
  "St. Elizabeth", "Westmoreland", "Hanover", "St. James", "Trelawny",
  "St. Ann", "St. Mary", "Portland", "St. Thomas",
];

export function BusinessQuickBooking() {
  const nav = useNavigate();
  const [pudos, setPudos] = useState([]);
  const [pudoId, setPudoId] = useState("");
  const [receiverName, setRn] = useState("");
  const [receiverPhone, setRp] = useState("");
  const [receiverEmail, setRe] = useState("");
  const [weight, setWeight] = useState(1);
  const [desc, setDesc] = useState("");
  const [payMode, setPayMode] = useState("sender_pays");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/business-quick/pudos/selectable").then(r => setPudos(r.data.rows || []))
      .catch((e) => {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          toast.error("Session expired"); logout(nav);
        }
      });
  }, [nav]);

  useEffect(() => {
    if (Number(weight) > 0) {
      api.post("/business-quick/packages/quote", { weight_lbs: Number(weight) })
        .then(r => setQuote(r.data)).catch(() => setQuote(null));
    }
  }, [weight]);

  const create = async () => {
    if (!pudoId) return toast.error("Select a PUDO");
    if (!receiverName || !receiverPhone) return toast.error("Receiver name + phone required");
    if (payMode === "receiver_pays" && !receiverEmail) return toast.error("Receiver email required for receiver-pays link");
    setBusy(true);
    try {
      const r = await api.post("/business-quick/packages", {
        receiver_name: receiverName, receiver_phone: receiverPhone, receiver_email: receiverEmail || undefined,
        destination_pudo_id: pudoId, weight_lbs: Number(weight), description: desc, payment_responsibility: payMode,
      });
      toast.success("Package created — awaiting payment");
      const pid = r.data?.package?.id;
      if (payMode === "sender_pays" && r.data?.payment_url) {
        // sender pays now: redirect to Fygaro
        window.location.href = r.data.payment_url;
        return;
      }
      nav(`/business-quick/package/${pid}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="business-quick-booking-page">
      <BizHeader title="New package" right={
        <Button size="sm" variant="outline" onClick={() => nav("/business-quick/dashboard")}>
          <ArrowLeft className="w-4 h-4 mr-1" />Back
        </Button>
      } />
      <div className="max-w-md mx-auto px-4 py-3 space-y-3">
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
          <div className="font-bold text-sm flex items-center gap-2"><Package className="w-4 h-4" />Destination</div>
          <select data-testid="bq-pudo-select" className="w-full border rounded p-2 text-sm" value={pudoId} onChange={e => setPudoId(e.target.value)}>
            <option value="">— select active PUDO —</option>
            {pudos.map(p => <option key={p.id} value={p.id}>{p.name} · {p.parish}</option>)}
          </select>
          {pudos.length === 0 && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              No active PUDOs. Contact admin to activate at least one drop-off station.
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-2">
          <div className="font-bold text-sm">Receiver</div>
          <Input data-testid="bq-receiver-name" placeholder="Name" value={receiverName} onChange={e => setRn(e.target.value)} />
          <Input data-testid="bq-receiver-phone" placeholder="Phone" value={receiverPhone} onChange={e => setRp(e.target.value)} />
          <Input data-testid="bq-receiver-email" type="email" placeholder="Email (required for receiver-pays)" value={receiverEmail} onChange={e => setRe(e.target.value)} />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-2">
          <div className="font-bold text-sm">Package</div>
          <label className="text-xs text-slate-500">Weight (lbs)</label>
          <Input data-testid="bq-weight" type="number" min={1} step={0.5} value={weight} onChange={e => setWeight(e.target.value)} />
          <label className="text-xs text-slate-500">Description</label>
          <Input data-testid="bq-desc" placeholder="e.g., Documents" value={desc} onChange={e => setDesc(e.target.value)} />
        </div>

        {quote && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm" data-testid="bq-quote">
            <div className="flex items-center gap-2 font-semibold"><Banknote className="w-4 h-4" /> Quote</div>
            <div>{quote.weight_lbs} lb · <strong>J${quote.total_jmd}</strong> (US${quote.usd_equivalent} @ {quote.exchange_rate})</div>
            <div className="text-xs text-slate-600">{quote.pricing_rule}</div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-2">
          <div className="font-bold text-sm">Payment</div>
          <div className="flex gap-2">
            <button data-testid="bq-pay-sender" onClick={() => setPayMode("sender_pays")} className={`flex-1 py-2 rounded-lg border text-sm ${payMode === "sender_pays" ? "bg-emerald-600 text-white border-emerald-700" : "bg-white"}`}>Sender pays now</button>
            <button data-testid="bq-pay-receiver" onClick={() => setPayMode("receiver_pays")} className={`flex-1 py-2 rounded-lg border text-sm ${payMode === "receiver_pays" ? "bg-emerald-600 text-white border-emerald-700" : "bg-white"}`}>Receiver pays</button>
          </div>
          <div className="text-[10px] text-slate-500">No cash at PUDO. Driver/PUDO see this package only after payment confirms.</div>
        </div>

        <Button data-testid="bq-create-submit" onClick={create} disabled={busy} className="w-full">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
          {payMode === "sender_pays" ? "Create & pay now" : "Create & email receiver"}
        </Button>
      </div>
    </div>
  );
}

export function BusinessQuickPackageDetail() {
  const nav = useNavigate();
  const { pid } = useParams();
  const [p, setP] = useState(null);
  const [label, setLabel] = useState(null);
  const [busy, setBusy] = useState(true);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const [pkgR, labelR] = await Promise.all([
        api.get(`/business-quick/packages/${pid}`),
        api.get(`/business-quick/packages/${pid}/label`).catch(() => ({ data: null })),
      ]);
      setP(pkgR.data);
      setLabel(labelR.data);
    } catch (e) {
      if (e?.response?.status === 401) { logout(nav); return; }
      toast.error("Could not load package");
    } finally { setBusy(false); }
  }, [pid, nav]);

  useEffect(() => { reload(); }, [reload]);

  const repurchase = async () => {
    try {
      const r = await api.post(`/business-quick/packages/${pid}/checkout`);
      if (r.data?.payment_url) { window.location.href = r.data.payment_url; }
      else toast.error("Could not generate payment URL");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendLink = async () => {
    const email = prompt("Receiver email", p?.receiver_email || "");
    if (!email) return;
    try {
      await api.post(`/business-quick/packages/${pid}/send-receiver-payment-link`, { receiver_email: email });
      toast.success(`Link sent to ${email}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
  };

  if (busy || !p) {
    return (
      <div className="min-h-screen bg-slate-50">
        <BizHeader title="Package" right={null} />
        <div className="text-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-1" />Loading…</div>
      </div>
    );
  }

  const isPaid = p.payment_status === "paid";
  const labelReady = label && label.status === "ready";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="bq-package-detail">
      <BizHeader title={`Package · ${p.tracking_number}`} right={
        <Button size="sm" variant="outline" onClick={() => nav("/business-quick/dashboard")}>
          <ArrowLeft className="w-4 h-4 mr-1" />Back
        </Button>
      } />
      <div className="max-w-md mx-auto px-4 py-3 space-y-3">
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-1 text-sm">
          <div className="flex items-center justify-between">
            <div className="font-bold">{p.tracking_number}</div>
            {statusPill(p)}
          </div>
          <div>To: <strong>{p.destination_pudo_name}</strong> ({p.destination_pudo_parish})</div>
          <div>Receiver: {p.receiver_name} · {p.receiver_phone}</div>
          {p.receiver_email && <div className="text-xs text-slate-500">{p.receiver_email}</div>}
          <div>Weight: {p.weight_lbs} lb · J${p.price} (US${p.usd_equivalent})</div>
          <div className="text-xs text-slate-500">Route: {p.route_class} · Visibility: {p.visibility_mode}</div>
          {p.payment_responsibility && (
            <div className="text-xs text-slate-500">Payment mode: {p.payment_responsibility}</div>
          )}
        </div>

        {!isPaid && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm" data-testid="bq-pay-card">
            <div className="flex items-center gap-2 font-semibold text-amber-900">
              <AlertCircle className="w-4 h-4" /> Awaiting payment
            </div>
            <div className="text-xs text-amber-800 mt-1">Driver and PUDO cannot see this package until payment confirms. Label is locked.</div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button data-testid="bq-pay-now" onClick={repurchase}>
                <CreditCard className="w-4 h-4 mr-1" />Pay now
              </Button>
              <Button data-testid="bq-send-link" variant="outline" onClick={sendLink}>
                <Send className="w-4 h-4 mr-1" />Send link
              </Button>
            </div>
          </div>
        )}

        {isPaid && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm">
            <div className="flex items-center gap-2 font-semibold text-emerald-900">
              <CheckCircle2 className="w-4 h-4" /> Payment confirmed
            </div>
            <div className="text-xs text-emerald-800 mt-1">A driver will pick this up and drop it at {p.destination_pudo_name}. Receiver will need the QR + PIN.</div>
          </div>
        )}

        {labelReady && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-2 print:shadow-none" id="bq-label-printable" data-testid="bq-label-ready">
            <div className="flex items-center justify-between">
              <div className="font-bold flex items-center gap-2"><Printer className="w-4 h-4" /> Label</div>
              <Button size="sm" variant="outline" onClick={() => window.print()}>
                <Printer className="w-4 h-4 mr-1" />Print
              </Button>
            </div>
            <div className="border border-slate-300 rounded p-3 text-xs space-y-1">
              <div className="font-bold text-base">{label.tracking_number}</div>
              <div>To: <strong>{label.destination_pudo_name}</strong> ({label.destination_pudo_parish})</div>
              <div>Receiver: {label.receiver_name} · {label.receiver_phone}</div>
              <div>Weight: {label.weight_lbs} lb · J${label.price_jmd}</div>
              {label.qr_code_url && (
                <div className="mt-2"><img src={label.qr_code_url} alt="QR" className="w-32 h-32" /></div>
              )}
              {label.receiver_pin && (
                <div className="text-[10px] text-slate-600">Receiver PIN (kept for receiver only): {label.receiver_pin}</div>
              )}
              <ul className="text-[10px] text-slate-500 mt-2 list-disc pl-4">
                {(label.instructions || []).map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          </div>
        )}

        {!isPaid && (
          <div className="text-center text-xs text-slate-400 flex items-center justify-center gap-1">
            <Clock className="w-3 h-3" /> Tracking visible to receiver after payment.
          </div>
        )}
      </div>
    </div>
  );
}
